import React, { createContext, useState, ReactElement, useMemo, useEffect } from 'react';

import { GET_COLLABORATION } from 'queries/getCollaboration.js';

import { setSession, tokenAuthenticatedPost } from 'utils/axios.js';

import * as Sentry from '@sentry/react';

import { CampaignProductEdge, Collaboration, MerchantSummary, Variant } from 'model/types/collaboration.js';

import { MAX_ITEMS_REQUESTED, SAMPLES_REQUEST_STEPS } from 'constants/samplesConstants.js';
import extractSampleReqUrlParams from '../utils/urlUtils.js';
import { ErrorState } from 'model/types/errors.js';

type SamplesContextProps = {
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  brand?: MerchantSummary;
  collaborationId?: string;
  error?: ErrorState;
  setError: React.Dispatch<React.SetStateAction<ErrorState | undefined>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  maxSamplesAllowed: number;
  variants: Variant[];
  selectedVariants: Variant[];
  setSelectedVariants: React.Dispatch<React.SetStateAction<Variant[]>>;
  steps: string[];
  handleNext: () => void;
  handleBack: () => void;
  triggerSubmit: boolean;
  setTriggerSubmit: React.Dispatch<React.SetStateAction<boolean>>;
};

type SamplesProviderProps = {
  readonly children: ReactElement;
};

// Create the context
const SamplesContext = createContext<SamplesContextProps | undefined>(undefined);

// Create a provider component
function SamplesProvider({ children }: SamplesProviderProps) {
  const [variants, setVariants] = useState<Variant[]>([]);
  const [brand, setBrand] = useState<MerchantSummary>();
  const [error, setError] = useState<ErrorState | undefined>();
  const [collaborationId, setCollaborationId] = useState<string | undefined>();
  const [selectedVariants, setSelectedVariants] = useState<Variant[]>([]);
  const [maxSamplesAllowed, setMaxSamplesAllowed] = useState<number>(0);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [triggerSubmit, setTriggerSubmit] = useState(false);

  const steps = SAMPLES_REQUEST_STEPS;

  useEffect(() => {
    const { collaborationId, token } = extractSampleReqUrlParams(window.location.href);
    if (!collaborationId || !token) {
      setIsLoading(false);
      setError({ message: 'you-need-to-be-invited' });
      setActiveStep(-1);
    }
    if (token) {
      setSession('Bearer', 'creatorToken', token);
    }
    if (collaborationId) {
      const asyncUseEffect = async () => {
        await fetchCollaborationData(collaborationId);
      };
      setCollaborationId(collaborationId);
      asyncUseEffect();
    }
  }, []);

  async function fetchCollaborationData(collabId: string) {
    try {
      const result = await tokenAuthenticatedPost({
        query: GET_COLLABORATION,
        vars: { id: collabId }
      });

      // Check if the response contains errors
      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors.map((error: any) => error.message).join(', ');
        throw new Error(`Failed to fetch collaboration data: ${errorMessage}`);
      }

      const collaboration: Collaboration = result.data.collaboration;

      console.log('Collaboration:', collaboration);

      // Store the brand name
      if (collaboration.merchantSummary) {
        const currentBrand = {
          name: collaboration.merchantSummary.name,
          logo: collaboration.merchantSummary.logo ?? '',
          logoBgColor: collaboration.merchantSummary.logoBgColor
        };
        setBrand(currentBrand);
      }

      // Identify how many samples are left
      const remainingSamples = collaboration.availableSamples;

      if (remainingSamples === undefined) {
        throw new Error('availableSamples was undefined');
      }
      if (remainingSamples === 0) {
        setError({ message: 'no-samples-available' });
        setActiveStep(-1);
        return;
      }

      setMaxSamplesAllowed(Math.min(remainingSamples, MAX_ITEMS_REQUESTED));

      // Store the Variants
      if (Array.isArray(collaboration.campaign?.campaignProducts?.edges)) {
        const newVariants = processCampaignVariants(collaboration.campaign?.campaignProducts?.edges || []);
        const mergedVariants = mergeAndDeduplicateVariants(variants, newVariants);
        setVariants(mergedVariants);
        handleVariantsCornerCases(newVariants);
      }
    } catch (e: any) {
      setIsLoading(false);
      setError({ message: 'something-went-wrong' });
      setActiveStep(-1);

      console.log('Error getting variants:', e.message);
      Sentry.captureException(`Error getting variants: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  }

  // Helper function to process campaign variants
  function processCampaignVariants(edges: CampaignProductEdge[]): Variant[] {
    const variants = edges.flatMap((edge) => {
      const product = edge.node.product;

      if (!product?.variants || product.variants.length === 0) {
        Sentry.captureException(new Error(`Product is missing variants: ${JSON.stringify(product)}`));
        return [];
      }

      return product.variants;
    });

    if (variants.length === 0) {
      throw new Error('No variants found for this collaboration');
    }

    return variants;
  }

  // Helper function to merge and deduplicate variants
  function mergeAndDeduplicateVariants(prevVariants: Variant[], newVariants: Variant[]): Variant[] {
    const uniqueVariants = [...new Map([...prevVariants, ...newVariants].map((p) => [p.id, p])).values()];

    if (uniqueVariants.length === 0) {
      Sentry.captureException(new Error('The final variants array is empty after processing.'));
    }

    return uniqueVariants;
  }

  const handleVariantsCornerCases = (variants: Variant[]) => {
    if (variants.length === 0) {
      setError({ message: 'no-samples-available' });
      setActiveStep(-1);
    }
    if (variants.length === 1) {
      setActiveStep(0);
      setSelectedVariants([variants[0]]);
    }
  };

  const handleNext = () => {
    if (activeStep === 1) {
      setTriggerSubmit(false);
      setTimeout(() => setTriggerSubmit(true), 0);
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  // Wrap the value object in useMemo
  const contextValue = useMemo(
    () => ({
      activeStep,
      setActiveStep,
      brand,
      collaborationId,
      error,
      setError,
      isLoading,
      setIsLoading,
      maxSamplesAllowed,
      variants,
      selectedVariants,
      setSelectedVariants,
      steps,
      handleNext,
      handleBack,
      triggerSubmit,
      setTriggerSubmit
    }),
    [activeStep, brand, isLoading, variants, selectedVariants, steps, handleNext, handleBack, triggerSubmit]
  );

  return <SamplesContext.Provider value={contextValue}>{children}</SamplesContext.Provider>;
}

export { SamplesProvider, SamplesContext };
